import { Flex, Line } from 'components';
import { H1 } from 'components/Typography/Typography';
import { Wrapper } from '../AdministrationAuditsView.styled';
import { useTranslation } from 'react-i18next';
import { AuditFormForm } from '../components/AuditFormForm/AuditFormForm';
import { useNavigate, useParams } from 'react-router-dom';
import { IAuditForm, IPostAuditFormRequest } from 'types/audits';
import { deleteAuditForm, getAuditForm, patchAuditForm } from 'api/audits';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { collapseSelectedAreaPermissionIds } from 'components/_form/NestedSelect/parseNestedSelectSelectedLabels';
import { findOptionByValueInTree } from 'components/_form/NestedSelect/utilities';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';
import { Loader } from 'components/Loader/Loader';
import { parseGroupsToAttributes } from 'utilities/auditForm/parseGroupsToAttributes';
import { parseRemovedGroups } from 'utilities/auditForm/parseRemovedGroups';
import { parseRemovedCriteria } from 'utilities/auditForm/parseRemovedCriteria';
import { showRequestError } from 'utilities/showRequestError';
import { onlyUniqueArray } from 'utilities/onlyUniqueArray';
import { DeleteAuditFormModal } from '../components/AuditFormsList/DeleteAuditFormModal';
import { toast } from 'react-toastify';

export const EditAuditFormView = () => {
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [defaultValues, setDefaultValues] = useState<
    Partial<IPostAuditFormRequest['audit_form']>
  >({});
  const [selectedAuditForm, setSelectedAuditForm] = useState<IAuditForm>();

  const { t } = useTranslation();

  const navigate = useNavigate();

  const { id: selectedAuditFormId } = useParams();
  const { data: areasData = [] } = useAreasQuery();
  const areaTreeRoots = useMemo(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );

  if (!selectedAuditFormId) return <></>;

  const handleSubmitAuditForm = async (
    data: IPostAuditFormRequest['audit_form']
  ) => {
    const { groups_attributes, criteria_attributes, approvers_attributes, ...rest } =
      data;

    const payload = {
      audit_form: {
        ...rest,
        groups_attributes: parseRemovedGroups(
          defaultValues.groups_attributes || [],
          groups_attributes
        ),
        criteria_attributes: parseRemovedCriteria(
          defaultValues.criteria_attributes || [],
          criteria_attributes
        ),
        approvers_attributes: [
          ...(approvers_attributes || []),
          ...(defaultValues.approvers_attributes
            ?.filter(
              (approver) =>
                !approvers_attributes?.find(
                  (attribute) =>
                    attribute.id?.toString() === approver.id?.toString()
                )
            )
            .map((approver) => ({
              id: approver.id,
              _destroy: 1
            })) || [])
        ]
      }
    };

    const auditFormRes = await patchAuditForm(selectedAuditFormId, payload);

    if ('error' in auditFormRes) {
      showRequestError(auditFormRes.error, t);
      return;
    }

    navigate('/app/administration/audits');
  };

  const handleDeleteAuditForm = async () => {
    const deleteAuditFormRes = await deleteAuditForm(selectedAuditFormId);

    if (deleteAuditFormRes && 'error' in deleteAuditFormRes) {
      showRequestError(deleteAuditFormRes.error, t);
      return;
    }

    toast.success(t('common.success'));
    navigate('/app/administration/audits');
  };

  const handleLoadData = useCallback(async () => {
    setLoading(true);
    const auditFormRes = await getAuditForm(selectedAuditFormId);

    if ('error' in auditFormRes) {
      setLoading(false);
      return;
    }

    setSelectedAuditForm(auditFormRes.data);

    const {
      areas,
      practice,
      author,
      people_authorized_to_edit,
      maximum_task_completion_time_iso8601,
      rating_scale,
      criteria,
      groups,
      methodology,
      pinned_to_type,
      pinned_to_id,
      execution_progress_tracking,
      approvers,
      ...rest
    } = auditFormRes.data;

    const areaOptionsFromApi = areas
      .map(({ id }) => findOptionByValueInTree(areaTreeRoots, String(id)))
      .filter((o): o is NonNullable<typeof o> => !!o);

    setDefaultValues({
      ...rest,
      area_ids: collapseSelectedAreaPermissionIds(
        areaTreeRoots,
        areaOptionsFromApi
      ).map((id) => String(id)),
      pinned_to_type: pinned_to_type || (practice ? 'Practice' : 'Methodology'),
      pinned_to_id: String(pinned_to_id || practice?.id || methodology?.id || ''),
      author_id: author?.id?.toString(),
      person_authorized_to_edit_ids: people_authorized_to_edit?.map(({ id }) =>
        id.toString()
      ),
      closing_ids: onlyUniqueArray(
        auditFormRes.data.audit_forms_closings
          ?.map((closing) => closing.user?.id?.toString())
          .filter((id): id is string => !!id) || []
      ),
      maximum_task_completion_time: maximum_task_completion_time_iso8601,
      rating_scale_id: rating_scale?.id?.toString(),
      criteria_attributes: criteria.map((criterion) => ({ ...criterion })),
      groups_attributes: parseGroupsToAttributes(groups),
      requires_approval: !!rest.requires_approval,
      in_this_order: !!rest.in_this_order,
      execution_progress_tracking_id: execution_progress_tracking?.id ?? null,
      approvers_attributes:
        approvers?.map((approver) => ({
          ...approver,
          execution_progress_tracking_approval_steps_attributes:
            approver.execution_progress_tracking_approval_steps
        })) ?? []
    });
    setLoading(false);
  }, [selectedAuditFormId, areaTreeRoots]);

  useEffect(() => {
    handleLoadData();
  }, [handleLoadData]);

  return (
    <Wrapper flexDirection="column">
      <Flex width="100%">
        <H1 m={2} variant={['h3', 'h2']}>
          {t('administrationAuditsView.editAuditForm')}
        </H1>
      </Flex>
      <Line />

      {loading ? (
        <Loader />
      ) : (
        <AuditFormForm
          handleSubmitAuditForm={handleSubmitAuditForm}
          defaultValues={defaultValues}
          selectedAuditForm={selectedAuditForm}
          setSelectedAuditForm={setSelectedAuditForm}
          showCopyButton
          isEditMode
          onDeleteClick={() => {
            setIsDeleteModalOpen(true);
          }}
        />
      )}

      <DeleteAuditFormModal
        isOpen={isDeleteModalOpen}
        onCancelClick={() => {
          setIsDeleteModalOpen(false);
        }}
        onSubmit={handleDeleteAuditForm}
      />
    </Wrapper>
  );
};
